// --- Shared dashboard helper functions and constants ---

export const MANAGER_ROLES = ['sales manager', 'senior manager'];
export const EXECUTIVE_ROLES = ['executive', 'company admin', 'admin'];

export const hasRole = (user, roleList) => {
    if (!user) return false;
    const role = user.role?.toLowerCase();
    const userType = user.userType?.toLowerCase();
    return roleList.includes(role) || roleList.includes(userType);
};

export const isManagerRole = (userType) =>
    userType === 'Sales Manager' || userType === 'Senior Manager';

export const getScoreColor = (score) => {
    if (score >= 2.5) return '#10b981';
    if (score >= 1.5) return '#f59e0b';
    return '#ef4444';
};

export const getScoreDisplay = (score) => {
    if (score >= 2.5) return 'Excellent';
    if (score >= 1.5) return 'Good';
    return 'Needs Work';
};

export const getProfileColor = (userType) => {
    const colorMap = {
        'Sales Manager': '#3498db',
        'Senior Manager': '#2980b9',
        'Sales Member': '#ff9500',
        'Company Admin': '#e74c3c',
        'Executive': '#27ae60',
        'admin': '#9b59b6'
    };
    return colorMap[userType] || '#95a5a6';
};

export const filterBySessionType = (assessments, sessionType) =>
    assessments.filter(a => {
        if (sessionType === 'Sales Presentation') {
            return a.sessionType === 'Sales Presentation' || a.sessionType === 'Sales Presentation / Demo';
        }
        return a.sessionType === sessionType;
    });

export const GRADE_MAP = { 'green': '#28a745', 'yellow': '#ffc107', 'red': '#dc3545' };
export const GRADE_VALUES = { 'green': 3, 'yellow': 2, 'red': 1 };

export const degradeGrade = (grade, levels) => {
    const order = ['green', 'yellow', 'red'];
    const idx = order.indexOf(grade);
    if (idx === -1) return 'red';
    return order[Math.min(idx + levels, 2)];
};

const GRADE_TO_BADGE = { '#28a745': 'success', '#ffc107': 'warning', '#dc3545': 'danger' };

export const getGradeBadgeVariant = (sessionType, userAssessments) => {
    const color = getBorderColor(sessionType, userAssessments);
    return GRADE_TO_BADGE[color] || null;
};

export const getBorderColor = (sessionType, userAssessments) => {
    const typeAssessments = filterBySessionType(userAssessments, sessionType);
    if (typeAssessments.length === 0) return '#e0e0e0';

    const sorted = [...typeAssessments].sort((a, b) =>
        new Date(b.completedDateTime) - new Date(a.completedDateTime)
    );
    const lastAssessment = sorted[0];
    const _n = new Date();
    const _l = new Date(lastAssessment.completedDateTime);
    const daysSince = Math.round((new Date(_n.getFullYear(), _n.getMonth(), _n.getDate()) - new Date(_l.getFullYear(), _l.getMonth(), _l.getDate())) / (1000 * 60 * 60 * 24));

    let finalGrade = lastAssessment.grade;
    if (!finalGrade || finalGrade === 'notGraded') return '#e0e0e0';
    const isLongCycle = sessionType === 'Sales Presentation';
    const threshold1 = isLongCycle ? 30 : 7;
    const threshold2 = isLongCycle ? 60 : 14;

    if (daysSince > threshold2) finalGrade = degradeGrade(finalGrade, 2);
    else if (daysSince > threshold1) finalGrade = degradeGrade(finalGrade, 1);

    return GRADE_MAP[finalGrade] || '#e0e0e0';
};

export const getLightBackgroundColor = (hexColor) => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, 0.12)`;
};

export const getMemberCardColor = (assessmentsStore, userId, companyId) => {
    if (!assessmentsStore?.closedAssessmentsForUser) return '#e0e0e0';
    try {
        // Company-scoped: without companyId a multi-company member's card
        // mixed every company's sessions while the charts beside it did not.
        const userAssessments = assessmentsStore.closedAssessmentsForUser(userId, companyId);
        if (!userAssessments || userAssessments.length === 0) return '#e0e0e0';

        const sorted = [...userAssessments].sort((a, b) =>
            new Date(b.completedDateTime) - new Date(a.completedDateTime)
        );
        const last = sorted[0];
        if (!last?.completedDateTime || !last?.grade || last.grade === 'notGraded') return '#e0e0e0';

        const _n2 = new Date();
        const _l2 = new Date(last.completedDateTime);
        const daysSince = Math.round((new Date(_n2.getFullYear(), _n2.getMonth(), _n2.getDate()) - new Date(_l2.getFullYear(), _l2.getMonth(), _l2.getDate())) / (1000 * 60 * 60 * 24));
        let finalGrade = last.grade;
        if (daysSince > 14) finalGrade = degradeGrade(finalGrade, 2);
        else if (daysSince > 7) finalGrade = degradeGrade(finalGrade, 1);

        return GRADE_MAP[finalGrade] || '#e0e0e0';
    } catch { return '#e0e0e0'; }
};

export const getStatusInfo = (daysSinceLastPractice) => {
    if (daysSinceLastPractice === null || daysSinceLastPractice === undefined) {
        return { text: 'Never Practiced', color: '#6c757d' };
    }
    if (daysSinceLastPractice === 0) return { text: 'Completed Today', color: '#28a745' };
    if (daysSinceLastPractice === 1) return { text: 'Completed Yesterday', color: '#ffc107' };
    if (daysSinceLastPractice <= 7) return { text: `${daysSinceLastPractice}d ago`, color: '#17a2b8' };
    if (daysSinceLastPractice <= 30) return { text: `${daysSinceLastPractice}d ago`, color: '#fd7e14' };
    return { text: `${daysSinceLastPractice}d ago`, color: '#dc3545' };
};

export const calculateUserPerformance = (assessmentsStore, userId, companyId) => {
    const empty = { avgScore: 0, totalPractices: 0, practicesLast30Days: 0, daysSinceLastPractice: null };
    if (!assessmentsStore?.closedAssessmentsForUser) return empty;

    try {
        // Company-scoped for the same reason as getMemberCardColor above.
        const userAssessments = assessmentsStore.closedAssessmentsForUser(userId, companyId);
        if (!userAssessments || userAssessments.length === 0) return empty;

        const now = new Date();
        const gradeToScore = { 'red': 1, 'yellow': 2, 'green': 3 };
        const scored = userAssessments
            .filter(a => a.grade != null && a.grade !== 'notGraded' && a.completedDateTime)
            .map(a => {
                const _cdt = new Date(a.completedDateTime);
                const _tMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const _cMid = new Date(_cdt.getFullYear(), _cdt.getMonth(), _cdt.getDate());
                const daysAgo = Math.round((_tMid - _cMid) / (1000 * 60 * 60 * 24));
                // daysAgo < 0 = future-dated bad data; don't give it top weight
                const weight = daysAgo < 0 ? 0 : daysAgo <= 30 ? 3 : daysAgo <= 60 ? 2 : daysAgo <= 90 ? 1 : 0;
                return { score: gradeToScore[a.grade] || 0, weight };
            })
            .filter(s => s.weight > 0);

        let avgScore = 0;
        if (scored.length > 0) {
            const totalWeight = scored.reduce((sum, s) => sum + s.weight, 0);
            avgScore = scored.reduce((sum, s) => sum + s.score * s.weight, 0) / totalWeight;
        }

        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const practicesLast30Days = userAssessments.filter(a =>
            a.completedDateTime && new Date(a.completedDateTime) >= thirtyDaysAgo
        ).length;

        let daysSinceLastPractice = null;
        const sorted = [...userAssessments].sort((a, b) =>
            new Date(b.completedDateTime) - new Date(a.completedDateTime)
        );
        if (sorted[0]?.completedDateTime) {
            const _now = new Date();
            const _last = new Date(sorted[0].completedDateTime);
            const _todayMid = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate());
            const _lastMid = new Date(_last.getFullYear(), _last.getMonth(), _last.getDate());
            daysSinceLastPractice = Math.round((_todayMid - _lastMid) / (1000 * 60 * 60 * 24));
        }

        return { avgScore, totalPractices: userAssessments.length, practicesLast30Days, daysSinceLastPractice };
    } catch (error) {
        console.error('Error calculating user performance for user', userId, error);
        return empty;
    }
};

export const mapUserToTeamMember = (user, assessmentsStore, usersStore, companyId) => {
    const perf = calculateUserPerformance(assessmentsStore, user.userId, companyId);
    let teamSize;
    if (isManagerRole(user.userType) || isManagerRole(user.role)) {
        try {
            teamSize = usersStore?.getDirectReports?.(user.userId)?.length ?? 0;
        } catch { teamSize = 0; }
    }
    return {
        id: user.userId,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User',
        role: user.userType || 'Unknown Role',
        avgScore: perf.avgScore,
        totalPractices: perf.totalPractices,
        practicesLast30Days: perf.practicesLast30Days,
        daysSinceLastPractice: perf.daysSinceLastPractice,
        profileColor: getProfileColor(user.userType),
        cardColor: getMemberCardColor(assessmentsStore, user.userId, companyId),
        teamSize,
        reportsToId: user.reportsToId
    };
};

export const calculateGoalProgress = (assessments, goals) => {
    const empty = { weeklyProgress: 0, weeklyTarget: 0, weeklyPercent: 0, monthlyProgress: 0, monthlyTarget: 0, monthlyPercent: 0, isOnTrack: false };
    if (!goals || (!goals.weeklyTarget && !goals.monthlyTarget)) return empty;

    const now = new Date();
    // Weekly: Monday-based week (UTC to match streak calculation)
    const dayOfWeek = now.getUTCDay();
    const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const weekStart = new Date(now);
    weekStart.setUTCDate(now.getUTCDate() - mondayOffset);
    weekStart.setUTCHours(0, 0, 0, 0);

    // Monthly
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const weeklyProgress = assessments.filter(a =>
        a.completedDateTime && new Date(a.completedDateTime) >= weekStart
    ).length;
    const monthlyProgress = assessments.filter(a =>
        a.completedDateTime && new Date(a.completedDateTime) >= monthStart
    ).length;

    const weeklyTarget = goals.weeklyTarget || 0;
    const monthlyTarget = goals.monthlyTarget || 0;
    const weeklyPercent = weeklyTarget > 0 ? Math.min(100, Math.round((weeklyProgress / weeklyTarget) * 100)) : 0;
    const monthlyPercent = monthlyTarget > 0 ? Math.min(100, Math.round((monthlyProgress / monthlyTarget) * 100)) : 0;

    const isOnTrack = (weeklyTarget > 0 && weeklyProgress >= weeklyTarget) ||
        (monthlyTarget > 0 && monthlyProgress >= monthlyTarget);

    return { weeklyProgress, weeklyTarget, weeklyPercent, monthlyProgress, monthlyTarget, monthlyPercent, isOnTrack };
};

export const STREAK_MESSAGES = [
    "Every expert was once a beginner. You're building excellence one practice at a time!",
    "Consistency is the mother of mastery. Your dedication is paying off!",
    "Success is the sum of small efforts repeated day in and day out.",
    "You're not just practicing, you're investing in your future success!",
    "Champions don't become champions in the ring. They become champions in their training!",
];

export const NO_STREAK_MESSAGES = [
    "The best time to plant a tree was 20 years ago. The second best time is now!",
    "Every journey begins with a single step. Start your streak today!",
    "You're one practice session away from starting something amazing!",
    "Excellence is a habit. Build yours starting today!",
    "The only way to get started is to quit talking and begin doing!",
];
